import React from "react";
import styled from "styled-components";
import {
  FaInstagram,
  FaFacebook,
  FaTiktok,
  FaLinkedin,
  FaArrowUp,
} from "react-icons/fa";
import { BsTwitterX } from "react-icons/bs";
import { Link } from "react-router-dom"; // Import du Link pour la navigation

// On attend des objets { label, link } dans items
const NavigationColumn = ({ title, items }) => (
  <Column>
    <ColumnTitle>{title}</ColumnTitle>
    {items.map((item, index) => (
      <ColumnItem key={index} as={Link} to={item.link}>
        {item.label}
      </ColumnItem>
    ))}
  </Column>
);

const Column = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-size: 18px;
  color: var(--TEXT-CLR, rgb(229, 229, 235));
  font-weight: 500;
  line-height: 124.5%;
  margin: auto 0;
`;

const ColumnTitle = styled.h3`
  color: var(--black, #080809);
  font: 700 21px Poppins, sans-serif;
`;

const ColumnItem = styled.a`
  font-family: Poppins, sans-serif;
  margin-top: 15px;
  cursor: pointer;
  text-decoration: none;
  color: var(--black, #080809);

  &:hover {
    text-decoration: underline;
  }
`;

const Logo = () => (
  <LogoWrapper>
    <LogoImage loading="lazy" src="./logo512.png" alt="Toyaloo Logo" />
    <Tagline>Nous réinventons l'apprentissage.</Tagline>
  </LogoWrapper>
);

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-size: 13px;
  color: var(--TEXT-CLR, #5e6282);
  font-weight: 500;
  line-height: 124.5%;
`;

const LogoImage = styled.img`
  aspect-ratio: 1.11;
  object-fit: auto;
  object-position: center;
  width: 163px;
  max-width: 100%;
`;

const Tagline = styled.p`
  font-family: Poppins, sans-serif;
  font-size: 18px;
  color: var(--TEXT-CLR, rgb(10, 10, 10));
`;

const SocialWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const ScrollToTop = styled.button`
  background: #ff4592;
  color: #ff4592;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  transition: all 0.3s;
  opacity: 0.8;
  z-index: 1000;

  &:hover {
    opacity: 1;
    transform: translateY(-5px);
  }
`;

const DownloadSection = () => (
  <DownloadWrapper>
    <SocialWrapper>
      <a
        href="https://www.instagram.com/toyaloo_?igsh=dW1xZW5rOWdlem1q"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaInstagram size={30} color="#000" />
      </a>
      <a
        href="https://www.facebook.com/share/1BZ7DJQ4oW/?mibextid=wwXIfr"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaFacebook size={30} color="#000" />
      </a>
      <a
        href="https://twitter.com/votre_compte"
        target="_blank"
        rel="noopener noreferrer"
      >
        <BsTwitterX size={30} color="#000" />
      </a>
      <a
        href="https://www.tiktok.com/@votre_compte"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaTiktok size={30} color="#000" />
      </a>
      <a
        href="https://www.linkedin.com/company/toyaloo/"
        target="_blank"
        rel="noopener noreferrer"
      >
        <FaLinkedin size={30} color="#000" />
      </a>
    </SocialWrapper>
    <DownloadText>
      Télécharger l'application <br /> mobile
    </DownloadText>
    <StoreButtons>
      <a
        href="https://apps.apple.com/app/toyaloo" // Remplace par ton vrai lien App Store
        target="_blank"
        rel="noopener noreferrer"
      >
        <StoreButton
          loading="lazy"
          src="./Assets/app_store.svg"
          alt="App Store"
        />
      </a>
      <a
        href="https://play.google.com/store/apps/details?id=com.toyaloo.app" // Remplace par ton vrai lien Play Store
        target="_blank"
        rel="noopener noreferrer"
      >
        <StoreButton
          loading="lazy"
          src="./Assets/play_store.svg"
          alt="Google Play"
        />
      </a>
    </StoreButtons>
  </DownloadWrapper>
);

const DownloadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 57px;
  @media (max-width: 991px) {
    margin-top: 40px;
  }
`;

const DownloadText = styled.p`
  color: var(--TEXT-CLR, rgb(10, 10, 10));
  text-align: center;
  letter-spacing: 0.1px;
  margin-top: 29px;
  font: 500 20px/25px Poppins, sans-serif;
`;

const StoreButtons = styled.div`
  display: flex;
  gap: 7px;
  margin: 16px 19px 0;
  justify-content: center;
  @media (max-width: 991px) {
    margin: 0 10px;
  }
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
`;

const StoreButton = styled.img`
  aspect-ratio: 3.13;
  object-fit: auto;
  object-position: center;
  width: 107px;
  max-width: 100%;
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const currentYear = new Date().getFullYear();

  // Mise à jour de la structure pour intégrer label et link
  const navigationData = [
    {
      title: "Pages",
      items: [
        { label: "A Propos", link: "/infos" },
        { label: "Connexion", link: "https://app.toyaloo.africa/" },
        { label: "Aloo", link: "/aloo" },
      ],
    },
    {
      title: "Contact",
      items: [
        { label: "Kinshasa, RDC", link: "/Infos" },
        { label: "+243000000000", link: "tel:+243000000000" },
        { label: "contact@toyaloo.africa", link: "mailto:contact@toyaloo.africa" },
      ],
    },
    {
      title: "A propos",
      items: [
        { label: "Abonnement", link: "/subscription" },
        { label: "Toyaloo team", link: "/Infos" },
        { label: "En savoir plus", link: "/Infos" },
      ],
    },
  ];

  return (
    <FooterWrapper>
      <FooterContent>
        <Logo />
        {navigationData.map((column, index) => (
          <NavigationColumn
            key={index}
            title={column.title}
            items={column.items}
          />
        ))}
        <DownloadSection />
      </FooterContent>
      <Copyright>
        toyaloo.africa &copy; {currentYear}
      </Copyright>

      <ScrollToTop onClick={scrollToTop} aria-label="Retour en haut">
        <FaArrowUp size={15} color="#ffffff" />
      </ScrollToTop>
    </FooterWrapper>
  );
};

const FooterWrapper = styled.footer`
  background:rgb(246, 246, 245);
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  align-items: center;
  text-align: center;
  @media (max-width: 600px) {
    padding: 0 10px;
  }
`;

const FooterContent = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 20px;
  justify-content: space-between;
  @media (max-width: 991px) {
    max-width: 100%;
    flex-wrap: wrap;
  }
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: var(--TEXT-CLR, rgb(10, 10, 10));
  align-self: center;
  margin-top: 39px;
  font: 500 14px/124.5% Poppins, sans-serif;
`;

export default Footer;
